import { useSearchParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import CourseManagement from '@/components/admin/CourseManagement'
import FreelancerProfileManagement from '@/components/admin/FreelancerProfileManagement'
import ClientProfileManagement from '@/components/admin/ClientProfileManagement'
import SearchHistory from '@/components/admin/SearchHistory'
import SkillsManagement from '@/components/admin/SkillsManagement'
import TagsManagement from '@/pages/admin/TagsManagement'
import { ChatAnalytics } from '@/components/admin/ChatAnalytics'
import { SlideIn, FadeIn } from '@/components/motion'
import { ChatConfig } from '@/components/admin/ChatConfig'
import { AdminChatViewer } from '@/components/admin/AdminChatViewer'
import { AdminChatView } from '@/components/admin/AdminChatView'
import { Button } from '@/components/ui/button'
import { SideDrawer, SideDrawerContent } from '@/components/ui/side-drawer'
import { X, MessageCircle } from 'lucide-react'

const tabTitles: Record<string, { title: string; subtitle: string }> = {
  freelancers: {
    title: 'Genius Management',
    subtitle: 'Review, approve, and manage AI expert applications',
  },
  clients: {
    title: 'Client Management',
    subtitle: 'Manage client accounts and project requests',
  },
  courses: {
    title: 'Course Management',
    subtitle: 'Create, edit, and manage training courses',
  },
  searches: {
    title: 'Search History',
    subtitle: 'View and analyze user search patterns',
  },
  skills: {
    title: 'Skills Management',
    subtitle: 'Manage skill categories and tags',
  },
  tags: {
    title: 'Tags Management',
    subtitle: 'Manage conversation tags and assignments',
  },
  'chat-analytics': {
    title: 'Chat Analytics',
    subtitle: 'View conversation statistics and engagement metrics',
  },
  'chat-config': {
    title: 'Chat Config',
    subtitle: 'Configure AI settings and system prompts',
  },
  'chat-viewer': {
    title: 'Chat Viewer',
    subtitle: 'View all conversations with analysis data',
  },
}

interface ConversationFilters {
  startDate?: string;
  endDate?: string;
  flagged?: boolean;
  role?: 'genius' | 'client';
  sentiment?: 'positive' | 'neutral' | 'negative';
  engagement?: 'low' | 'medium' | 'high';
  tags?: string[];
}

export default function AdminPage() {
  const [searchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'freelancers'
  const [startDateInput, setStartDateInput] = useState<string>('')
  const [endDateInput, setEndDateInput] = useState<string>('')
  const [appliedStartDate, setAppliedStartDate] = useState<string>('')
  const [appliedEndDate, setAppliedEndDate] = useState<string>('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerFilters, setDrawerFilters] = useState<ConversationFilters | undefined>(undefined)
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)

  const currentTabInfo = tabTitles[activeTab] || tabTitles.freelancers

  // Helper function to get default last 30 days dates
  const getDefaultDates = () => {
    const today = new Date()
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(today.getDate() - 30)
    
    const formatDate = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    
    return {
      startDate: formatDate(thirtyDaysAgo),
      endDate: formatDate(today)
    }
  }

  // Set default dates to last 30 days when chat-analytics tab is active
  useEffect(() => {
    if (activeTab === 'chat-analytics' && !startDateInput && !endDateInput) {
      const { startDate, endDate } = getDefaultDates()
      setStartDateInput(startDate)
      setEndDateInput(endDate)
      setAppliedStartDate(startDate)
      setAppliedEndDate(endDate)
    }
  }, [activeTab, startDateInput, endDateInput])

  const applyDateFilter = () => {
    setAppliedStartDate(startDateInput)
    setAppliedEndDate(endDateInput)
  }

  const resetToDefaultDates = () => {
    const { startDate, endDate } = getDefaultDates()
    setStartDateInput(startDate)
    setEndDateInput(endDate)
    setAppliedStartDate(startDate)
    setAppliedEndDate(endDate)
  }

  // Check if current dates match default last 30 days
  const isDefaultDateRange = () => {
    if (!startDateInput || !endDateInput) return false
    const { startDate, endDate } = getDefaultDates()
    return startDateInput === startDate && endDateInput === endDate
  }

  const isDefaultRange = isDefaultDateRange()
  const showDateFilter = activeTab === 'chat-analytics'

  const handleOpenDrawer = (filters?: ConversationFilters) => {
    setDrawerFilters(filters)
    setDrawerOpen(true)
    setSelectedConversationId(null)
  }

  const handleDrawerOpenChange = (open: boolean) => {
    setDrawerOpen(open)
    if (!open) {
      setDrawerFilters(undefined)
      setSelectedConversationId(null)
    }
  }

  const handleConversationSelect = (conversationId: string) => {
    setSelectedConversationId(conversationId)
  }

  return (
    <div className="h-[calc(100vh-82px)] bg-slate-50">
      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Page Title */}
        <SlideIn direction="down">
          <div className="mb-6 sm:mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-[32px] font-black text-slate-900 tracking-tight">
                {currentTabInfo.title}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                {currentTabInfo.subtitle}
              </p>
            </div>
            {showDateFilter && (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 bg-white rounded-[40px] border border-slate-200 px-4 h-10 shadow-sm hover:shadow-md transition-shadow relative">
                  <input
                    type="date"
                    value={startDateInput}
                    onChange={(e) => setStartDateInput(e.target.value)}
                    className="h-8 w-[140px] text-sm bg-transparent border-none outline-none focus:ring-0 text-slate-700 cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:saturate-0 [&::-webkit-calendar-picker-indicator]:invert-[0.6]"
                  />
                  <span className="text-slate-400 text-sm flex-shrink-0">-</span>
                  <input
                    type="date"
                    value={endDateInput}
                    onChange={(e) => setEndDateInput(e.target.value)}
                    min={startDateInput}
                    className="h-8 w-[140px] text-sm bg-transparent border-none outline-none focus:ring-0 text-slate-700 cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:saturate-0 [&::-webkit-calendar-picker-indicator]:invert-[0.6]"
                  />
                  {isDefaultRange ? (
                    <span className="text-xs text-slate-400 ml-1 whitespace-nowrap flex-shrink-0">Last 30d</span>
                  ) : (
                    <button
                      onClick={resetToDefaultDates}
                      className="ml-1 text-slate-400 hover:text-slate-600 flex-shrink-0 transition-colors"
                      aria-label="Reset to last 30 days"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Button
                  onClick={applyDateFilter}
                  variant="outline"
                  className="rounded-[40px] h-10 px-5 border-slate-200 hover:bg-slate-50"
                  size="sm"
                >
                  Apply
                </Button>
                <Button
                  onClick={() => handleOpenDrawer()}
                  className="rounded-[40px] h-10 px-5 bg-[#4669ed] hover:bg-[#3a5ad9]"
                  size="sm"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  View Conversations
                </Button>
              </div>
            )}
          </div>
        </SlideIn>

        {/* Tab Content */}
        <FadeIn delay={0.1}>
          <div className="overflow-x-auto">
            {activeTab === 'freelancers' && <FreelancerProfileManagement />}
            {activeTab === 'clients' && <ClientProfileManagement />}
            {activeTab === 'courses' && <CourseManagement />}
            {activeTab === 'searches' && <SearchHistory />}
            {activeTab === 'skills' && <SkillsManagement />}
            {activeTab === 'tags' && <TagsManagement />}
            {activeTab === 'chat-analytics' && (
              <ChatAnalytics 
                startDate={appliedStartDate}
                endDate={appliedEndDate}
                onOpenDrawer={handleOpenDrawer}
              />
            )}
            {activeTab === 'chat-config' && <ChatConfig />}
            {activeTab === 'chat-viewer' && <AdminChatViewer />}
          </div>
        </FadeIn>
      </div>

      {/* Conversations Drawer */}
      <SideDrawer open={drawerOpen} onOpenChange={handleDrawerOpenChange}>
        <SideDrawerContent
          className="p-0 overflow-hidden"
          showCloseButton={true}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-shrink-0 bg-white">
              <h2 className="text-lg font-semibold text-slate-900">Conversations</h2>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              {selectedConversationId ? (
                <div className="h-full">
                  <AdminChatView
                    conversationId={selectedConversationId}
                    onBack={() => setSelectedConversationId(null)}
                  />
                </div>
              ) : (
                <AdminChatViewer
                  initialFilters={drawerFilters}
                  onConversationSelect={handleConversationSelect}
                />
              )}
            </div>
          </div>
        </SideDrawerContent>
      </SideDrawer>
    </div>
  )
}
